/**
 * Collect The Flags · WC 2026 mode (iter 91)
 *
 * Single-file feature module — exports a default page component plus the
 * sub-screens it transitions through:
 *   Lobby → (Tutorial first-time) → Matchmake (squad + want-list + queue) →
 *   Match (attest + report) → Flag-reveal → back to Lobby.
 *
 * Album is rendered as a separate route via the named export <CtfAlbum/>.
 *
 * Routes (wired in App.js):
 *   /v2/wc2026/ctf            — Lobby (default export)
 *   /v2/wc2026/ctf/album      — Full 48-flag album
 *   /wc2026/ctf, /wc2026/ctf/album — V1 alias routes
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import {
  Loader2, ChevronLeft, Flag, Trophy, Sparkles, Search, X, Check,
  Smartphone, Monitor, Plus, Clock, Send, Camera, Award, ArrowRight,
} from "lucide-react";
import WcLockedNotice from "../../components/WcLockedNotice";
import useWcModeVisibility from "../../hooks/useWcModeVisibility";

/* =================================================================== */
/* Layout shell — header bar + content                                  */
/* =================================================================== */
function CtfShell({ children, backTo = "/v2/wc2026", title = "Collect The Flags" }) {
  return (
    <div className="min-h-screen bg-[#020617] text-white" data-testid="ctf-shell">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-5 pb-3 flex items-center justify-between">
        <Link to={backTo} data-testid="ctf-back" className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-white/55 hover:text-white">
          <ChevronLeft className="w-3.5 h-3.5" /> Back
        </Link>
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#60A5FA]">// {title}</div>
        <Link to="/v2/wc2026/ctf/album" data-testid="ctf-album-link" className="text-[10px] font-mono uppercase tracking-widest text-white/55 hover:text-[#60A5FA]">
          Album →
        </Link>
      </div>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 pb-16 space-y-4">{children}</div>
    </div>
  );
}

/* =================================================================== */
/* Default export: Lobby — entry point, drives the per-game flow.       */
/* =================================================================== */
export default function CtfHub() {
  const [platform, setPlatform] = useState("mobile");
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showTutorial, setShowTutorial] = useState(false);
  const [phase, setPhase] = useState("lobby"); // lobby | matchmake
  const nav = useNavigate();
  const loc = useLocation();
  // iter 93 — admin visibility gate per platform
  const vis = useWcModeVisibility("ctf", platform);

  const loadState = useCallback(async () => {
    try {
      const { data } = await api.get(`/ctf/state?platform=${platform}`);
      setState(data);
      if (!data.tutorial_seen) setShowTutorial(true);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [platform]);

  useEffect(() => { setLoading(true); loadState(); }, [loadState]);

  // Poll every 4s while in queue or active room
  useEffect(() => {
    if (!state) return;
    if (!state.in_queue && !state.active_room) return;
    const id = setInterval(loadState, 4000);
    return () => clearInterval(id);
  }, [state, loadState]);

  // If we have an active room, route to the match screen automatically.
  useEffect(() => {
    if (state?.active_room && !loc.pathname.includes("/match/")) {
      nav(`/v2/wc2026/ctf/match/${state.active_room.id}`, { replace: false });
    }
  }, [state?.active_room, loc.pathname, nav]);

  const onTutorialDone = async () => {
    setShowTutorial(false);
    try { await api.post(`/ctf/tutorial/complete?platform=${platform}`); }
    catch (e) { /* non-blocking */ }
    loadState();
  };

  if (loading && !state) {
    return <CtfShell><Loader2 className="w-6 h-6 animate-spin mx-auto mt-12 text-[#60A5FA]" /></CtfShell>;
  }

  return (
    <>
      <CtfShell>
        <PlatformSwitcher value={platform} onChange={setPlatform} />
        <CollectionHero collection={state.collection} platform={platform} />
        {!vis.effective ? (
          <WcLockedNotice accent="#60A5FA" />
        ) : state.in_queue ? (
          <QueueView state={state} onChange={loadState} platform={platform} />
        ) : phase === "matchmake" ? (
          <MatchmakePanel
            platform={platform}
            collection={state.collection}
            onCancel={() => setPhase("lobby")}
            onQueued={() => { setPhase("lobby"); loadState(); }}
          />
        ) : (
          <LobbyCallout
            collection={state.collection}
            onStart={() => setPhase("matchmake")}
          />
        )}
        {vis.effective && <AlbumPreview platform={platform} />}
      </CtfShell>
      {showTutorial && <CtfTutorial onDone={onTutorialDone} />}
    </>
  );
}

/* =================================================================== */
/* Platform switcher                                                    */
/* =================================================================== */
function PlatformSwitcher({ value, onChange }) {
  return (
    <div className="inline-flex items-center gap-1 rounded-full bg-white/5 border border-white/10 p-1" data-testid="ctf-platform-switch">
      {[
        { v: "mobile", l: "Mobile", I: Smartphone },
        { v: "console", l: "Console", I: Monitor },
      ].map(({ v, l, I }) => (
        <button
          key={v}
          onClick={() => onChange(v)}
          data-testid={`ctf-platform-${v}`}
          className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[10px] font-mono uppercase tracking-widest transition ${
            value === v ? "bg-[#60A5FA] text-[#020617]" : "text-white/60 hover:text-white"
          }`}
        >
          <I className="w-3 h-3" /> {l}
        </button>
      ))}
    </div>
  );
}

/* =================================================================== */
/* Collection hero with progress ring                                   */
/* =================================================================== */
function CollectionHero({ collection, platform }) {
  const c = collection || { count: 0, matches_played: 0, streak_count: 0, streak_best: 0 };
  const pct = Math.min(100, Math.round((c.count / 48) * 100));
  return (
    <div className="rounded-3xl bg-gradient-to-br from-[#1E3A8A] via-[#0F172A] to-[#020617] border border-[#60A5FA]/30 p-5 shadow-2xl" data-testid="ctf-hero">
      <div className="flex items-center gap-4">
        <div className="relative w-20 h-20 shrink-0">
          <svg className="absolute inset-0 -rotate-90" viewBox="0 0 80 80">
            <circle cx="40" cy="40" r="34" stroke="#1E3A8A" strokeWidth="7" fill="none" />
            <circle cx="40" cy="40" r="34" stroke="#60A5FA" strokeWidth="7" fill="none"
                    strokeDasharray={`${(pct/100) * 2 * Math.PI * 34} ${2 * Math.PI * 34}`}
                    strokeLinecap="round" style={{ transition: "stroke-dasharray 600ms" }} />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center font-heading text-2xl font-extrabold text-[#60A5FA]">
            {c.count}
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#60A5FA]/80">// Album</div>
          <div className="font-heading text-xl uppercase tracking-wider mt-0.5">{c.count}/48 flags</div>
          <div className="text-[11px] text-white/60 mt-1">
            {c.matches_played} match{c.matches_played === 1 ? "" : "es"} played · platform: <span className="text-white/90">{platform}</span>
          </div>
          {c.streak_count > 0 && (
            <div className="mt-1.5 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-[#FBBF24]/20 text-[#FBBF24] text-[10px] font-mono uppercase tracking-widest" data-testid="ctf-streak">
              <Sparkles className="w-3 h-3" /> Streak ×{c.streak_count} (best {c.streak_best})
            </div>
          )}
          {c.count >= 48 && (
            <div className="mt-1.5 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-[#FFD700] text-[#020617] text-[10px] font-mono uppercase tracking-widest font-bold" data-testid="ctf-complete">
              <Trophy className="w-3 h-3" /> 48/48 Complete
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* =================================================================== */
/* Lobby callout — "Find a match"                                       */
/* =================================================================== */
function LobbyCallout({ collection, onStart }) {
  return (
    <div className="rounded-2xl border border-[#60A5FA]/30 bg-white/5 p-5 text-center" data-testid="ctf-lobby-callout">
      <Flag className="w-10 h-10 text-[#60A5FA] mx-auto mb-3" />
      <div className="font-heading text-lg uppercase tracking-wider">Find a flag-trade partner</div>
      <div className="text-[11px] text-white/60 mt-1.5 max-w-md mx-auto leading-relaxed">
        Pick a national squad (pure 11) and up to 3 nations you want to collect. We&apos;ll match you with someone whose targets line up with yours.
      </div>
      <button
        type="button"
        onClick={onStart}
        data-testid="ctf-start-btn"
        className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#60A5FA] text-[#020617] font-mono text-[11px] uppercase tracking-widest hover:bg-[#3B82F6] transition-colors font-bold"
      >
        <Search className="w-3.5 h-3.5" /> Start a flag hunt
      </button>
    </div>
  );
}

/* =================================================================== */
/* Matchmake panel — squad + want-list selection                         */
/* =================================================================== */
function MatchmakePanel({ platform, collection, onCancel, onQueued }) {
  const [nations, setNations] = useState([]);
  const [squad, setSquad] = useState("");
  const [wants, setWants] = useState([]);
  const [busy, setBusy] = useState(false);
  const held = useMemo(() => new Set((collection?.flags || []).map((f) => f.nation)), [collection]);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/ctf/nations"); setNations(data.nations || []); }
      catch (e) { toast.error(formatApiError(e)); }
    })();
  }, []);

  const toggleWant = (n) => {
    if (n === squad) return; // can't hunt your own
    setWants((prev) => {
      if (prev.includes(n)) return prev.filter((x) => x !== n);
      if (prev.length >= 3) {
        toast.message("Up to 3 hunt-list nations");
        return prev;
      }
      return [...prev, n];
    });
  };

  const submit = async () => {
    if (!squad) { toast.error("Pick your squad nation"); return; }
    if (wants.length === 0) { toast.error("Pick at least one flag to hunt"); return; }
    setBusy(true);
    try {
      await api.post("/ctf/queue", { platform, squad_nation: squad, want_list: wants });
      toast.success("Joined the queue — finding a flag trade partner…");
      onQueued && onQueued();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="ctf-matchmake">
      {/* Squad nation picker */}
      <Section title="Your squad nation" desc="Pure 11 starters from this nation — bench composition free.">
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-1.5 max-h-72 overflow-y-auto pr-1">
          {nations.map((n) => (
            <button
              key={n.name}
              onClick={() => setSquad(n.name)}
              data-testid={`ctf-squad-${n.name}`}
              className={`flex flex-col items-center gap-1 rounded-lg px-2 py-2 border text-[10px] transition ${
                squad === n.name
                  ? "bg-[#60A5FA] text-[#020617] border-[#60A5FA] font-bold"
                  : "bg-white/5 text-white/70 border-white/10 hover:border-[#60A5FA]/60"
              }`}
            >
              <span className="text-xl leading-none">{n.flag}</span>
              <span className="truncate w-full">{n.name}</span>
              {n.rare && <span className="text-[8px] uppercase tracking-widest text-[#FBBF24]">Rare</span>}
            </button>
          ))}
        </div>
      </Section>

      {/* Want list */}
      <Section
        title="Flags you're hunting (up to 3)"
        desc="We'll match you with someone whose squad nation is in your list. ★ = rare nation bonus."
      >
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-1.5 max-h-72 overflow-y-auto pr-1">
          {nations.map((n) => {
            const has = held.has(n.name);
            const picked = wants.includes(n.name);
            const disabled = n.name === squad;
            return (
              <button
                key={n.name}
                onClick={() => toggleWant(n.name)}
                disabled={disabled}
                data-testid={`ctf-want-${n.name}`}
                className={`relative flex flex-col items-center gap-1 rounded-lg px-2 py-2 border text-[10px] transition ${
                  picked
                    ? "bg-[#10B981] text-[#020617] border-[#10B981] font-bold"
                    : has
                      ? "bg-white/5 text-white/30 border-white/10 line-through"
                      : disabled
                        ? "bg-white/[0.02] text-white/20 border-white/5"
                        : "bg-white/5 text-white/70 border-white/10 hover:border-[#10B981]/60"
                }`}
              >
                <span className="text-xl leading-none">{n.flag}</span>
                <span className="truncate w-full">{n.name}</span>
                {n.rare && <span className="text-[8px] uppercase tracking-widest text-[#FBBF24]">★ Rare</span>}
                {has && !picked && <span className="absolute top-1 right-1 text-[#10B981] text-[10px]">✓</span>}
              </button>
            );
          })}
        </div>
        <div className="mt-2 text-[10px] font-mono text-white/50">{wants.length}/3 picked · {held.size}/48 collected</div>
      </Section>

      <div className="flex items-center justify-between gap-2 pt-2">
        <button
          type="button"
          onClick={onCancel}
          data-testid="ctf-matchmake-cancel"
          className="px-4 py-2 rounded-full bg-white/5 text-white/60 hover:text-white text-[11px] font-mono uppercase tracking-widest"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={busy || !squad || wants.length === 0}
          data-testid="ctf-matchmake-submit"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#60A5FA] text-[#020617] text-[11px] font-mono uppercase tracking-widest font-bold disabled:opacity-40 hover:bg-[#3B82F6] transition-colors"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
          Find a partner
        </button>
      </div>
    </div>
  );
}

function Section({ title, desc, children }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
      <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#60A5FA]/80">// {title}</div>
      <div className="text-[11px] text-white/55 mt-1 mb-3">{desc}</div>
      {children}
    </div>
  );
}

/* =================================================================== */
/* Queue view — tier escalation visual                                  */
/* =================================================================== */
function QueueView({ state, onChange, platform }) {
  const [wait, setWait] = useState(state.queue_entry?.wait_secs || 0);
  useEffect(() => {
    const id = setInterval(() => setWait((w) => w + 1), 1000);
    return () => clearInterval(id);
  }, []);
  const tier = wait < 20 ? 1 : wait < 45 ? 2 : 3;
  const tierLabels = {
    1: "Looking for a full mutual trade (Tier 1) — both players gain a flag.",
    2: "Widening search (Tier 2) — opponent has a flag you want, even if you can't trade them one.",
    3: "Open match (Tier 3) — any opponent. You still earn their nation flag if new.",
  };

  const leave = async () => {
    try {
      await api.post(`/ctf/dequeue?platform=${platform}`);
      onChange && onChange();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const wantTags = (state.queue_entry?.want_list || []).join(" · ");

  return (
    <div className="rounded-2xl border border-[#FBBF24]/40 bg-gradient-to-br from-[#FBBF24]/10 to-transparent p-5 text-center space-y-3" data-testid="ctf-queue-view">
      <Loader2 className="w-8 h-8 text-[#FBBF24] mx-auto animate-spin" />
      <div className="font-heading text-base uppercase tracking-wider">Finding a flag-trade partner…</div>
      <div className="text-[11px] text-white/60">
        Your squad: <span className="text-white font-bold">{state.queue_entry?.squad_nation}</span>
        <br />Hunting: <span className="text-white">{wantTags}</span>
      </div>
      <div className="flex items-center justify-center gap-1.5 mt-2">
        {[1, 2, 3].map((t) => (
          <div
            key={t}
            data-testid={`ctf-tier-${t}`}
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-mono uppercase tracking-widest border transition ${
              t === tier
                ? "bg-[#FBBF24] text-[#020617] border-[#FBBF24] font-bold"
                : t < tier
                  ? "bg-white/10 text-white/30 border-white/10 line-through"
                  : "bg-white/5 text-white/40 border-white/10"
            }`}
          >
            T{t} {t === 1 ? "Ideal" : t === 2 ? "Partial" : "Open"}
          </div>
        ))}
      </div>
      <div className="text-[10px] text-white/40 max-w-md mx-auto">{tierLabels[tier]}</div>
      <div className="text-[10px] font-mono text-white/50 inline-flex items-center gap-1">
        <Clock className="w-3 h-3" /> Waiting {wait}s
      </div>
      <button
        type="button"
        onClick={leave}
        data-testid="ctf-leave-queue"
        className="mx-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white/70 hover:text-white text-[10px] font-mono uppercase tracking-widest"
      >
        <X className="w-3 h-3" /> Leave queue
      </button>
    </div>
  );
}

/* =================================================================== */
/* Album preview row (compact)                                          */
/* =================================================================== */
function AlbumPreview({ platform }) {
  const [grid, setGrid] = useState(null);
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get(`/ctf/album?platform=${platform}`); setGrid(data); }
      catch (e) { /* silent */ }
    })();
  }, [platform]);
  if (!grid) return null;
  const recent = (grid.grid || []).filter((g) => g.held).slice(-8).reverse();
  if (recent.length === 0) {
    return (
      <Link
        to="/v2/wc2026/ctf/album"
        data-testid="ctf-album-empty"
        className="block rounded-2xl border border-white/10 bg-white/5 p-4 text-center hover:border-[#60A5FA]/50"
      >
        <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-white/40">No flags yet</div>
        <div className="text-[11px] text-white/60 mt-1">Play your first match to start collecting → <span className="text-[#60A5FA]">View album</span></div>
      </Link>
    );
  }
  return (
    <Link
      to="/v2/wc2026/ctf/album"
      data-testid="ctf-album-preview"
      className="block rounded-2xl border border-white/10 bg-white/5 p-3 hover:border-[#60A5FA]/50 transition-colors"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#60A5FA]/80">// Recent flags</div>
        <div className="text-[10px] font-mono text-white/50">{grid.count}/48 · View album <ArrowRight className="inline w-3 h-3" /></div>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {recent.map((g) => (
          <div key={g.nation} className="inline-flex flex-col items-center w-12 text-center">
            <span className="text-xl">{g.flag}</span>
            <span className="text-[8px] font-mono text-white/50 truncate w-full">{g.nation}</span>
          </div>
        ))}
      </div>
    </Link>
  );
}

/* =================================================================== */
/* Full-screen first-time tutorial — 4 slides, skippable                */
/* =================================================================== */
function CtfTutorial({ onDone }) {
  const [slide, setSlide] = useState(0);
  const slides = [
    {
      icon: "🇧🇷🇫🇷🇪🇸",
      title: "Pure national squads only",
      body: "Pick one of the 48 World Cup nations. Your starting 11 must ALL be from that single nation. Bench composition is unrestricted.",
    },
    {
      icon: "🎯",
      title: "Pick up to 3 flags to hunt",
      body: "Each match, choose up to 3 nation flags you want to collect. We'll search the queue for someone with a matching squad.",
    },
    {
      icon: "🤝",
      title: "Mutual matchmaking",
      body: "Tier 1: full mutual trade — both players earn a flag. Tier 2 (20s+): you earn a flag, opponent may not. Tier 3 (45s+): any opponent, still a chance.",
    },
    {
      icon: "🏁",
      title: "Play to collect",
      body: "Win, draw, or loss — you earn the opponent's flag if it's new to your album. Goal: collect all 48 for the World Cup trophy reward.",
    },
  ];
  const s = slides[slide];
  const next = () => slide < slides.length - 1 ? setSlide(slide + 1) : onDone();
  return (
    <div className="fixed inset-0 z-[100] bg-[#020617]/95 backdrop-blur-md flex items-center justify-center p-6" data-testid="ctf-tutorial">
      <div className="max-w-md w-full rounded-3xl bg-gradient-to-br from-[#1E3A8A] to-[#020617] border border-[#60A5FA]/40 p-6 sm:p-8 text-center">
        <div className="text-5xl sm:text-6xl mb-4">{s.icon}</div>
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#60A5FA]/70">// Slide {slide + 1} of {slides.length}</div>
        <div className="font-heading text-xl uppercase tracking-wider mt-1">{s.title}</div>
        <div className="text-[12px] text-white/70 mt-3 leading-relaxed">{s.body}</div>
        <div className="mt-5 flex items-center justify-center gap-1">
          {slides.map((_, i) => (
            <div key={i} className={`w-1.5 h-1.5 rounded-full ${i === slide ? "bg-[#60A5FA]" : "bg-white/20"}`} />
          ))}
        </div>
        <div className="mt-5 flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={onDone}
            data-testid="ctf-tutorial-skip"
            className="px-3 py-2 rounded-full text-[10px] font-mono uppercase tracking-widest text-white/50 hover:text-white"
          >
            Skip
          </button>
          <button
            type="button"
            onClick={next}
            data-testid="ctf-tutorial-next"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#60A5FA] text-[#020617] text-[11px] font-mono uppercase tracking-widest font-bold hover:bg-[#3B82F6]"
          >
            {slide < slides.length - 1 ? <>Next <ArrowRight className="w-3 h-3" /></> : <>Start <Check className="w-3 h-3" /></>}
          </button>
        </div>
      </div>
    </div>
  );
}

/* =================================================================== */
/* Named export: <CtfMatch> — drives an active room                     */
/* =================================================================== */
export function CtfMatch() {
  const loc = useLocation();
  const nav = useNavigate();
  const roomId = loc.pathname.split("/").pop();
  const [room, setRoom] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [scoreMine, setScoreMine] = useState("");
  const [scoreOpp, setScoreOpp] = useState("");
  const [completion, setCompletion] = useState(100);
  const [showReveal, setShowReveal] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/ctf/room/${roomId}`); setRoom(data); }
    catch (e) { toast.error(formatApiError(e)); nav("/v2/wc2026/ctf"); }
    finally { setLoading(false); }
  }, [roomId, nav]);

  useEffect(() => { load(); }, [load]);
  // Poll for opponent's status
  useEffect(() => {
    if (!room) return;
    if (room.status === "closed" || room.status === "awarded") return;
    const id = setInterval(load, 4000);
    return () => clearInterval(id);
  }, [room, load]);

  // Trigger reveal once status becomes "awarded"
  useEffect(() => {
    if (room?.status === "awarded" && !showReveal) setShowReveal(true);
  }, [room?.status, showReveal]);

  const attest = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/ctf/room/${roomId}/attest`, { pure_squad: true });
      setRoom(data);
      toast.success("Squad attested — both players must confirm before kickoff");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const report = async (shotFile) => {
    if (scoreMine === "" || scoreOpp === "") { toast.error("Enter both scores"); return; }
    setBusy(true);
    try {
      let data;
      if (shotFile) {
        // iter 97 — attach the optional screenshot via the multipart endpoint
        const fd = new FormData();
        fd.append("my_score", String(Number(scoreMine)));
        fd.append("opp_score", String(Number(scoreOpp)));
        fd.append("completion_pct", String(Number(completion)));
        fd.append("screenshot", shotFile);
        ({ data } = await api.post(`/ctf/room/${roomId}/result-with-shot`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        }));
      } else {
        ({ data } = await api.post(`/ctf/room/${roomId}/result`, {
          my_score: Number(scoreMine), opp_score: Number(scoreOpp), completion_pct: Number(completion),
        }));
      }
      setRoom(data);
      toast.success("Score reported. Waiting for opponent…");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  // iter 97 — bail out of a room when the opponent isn't responding (rather
  // than being stuck until the 5/30-min timeout). Closes it for both players.
  const leave = async () => {
    if (!window.confirm("Leave this match? No flag is awarded and you'll return to the lobby.")) return;
    setBusy(true);
    try {
      await api.post(`/ctf/room/${roomId}/leave`);
      toast.message("Left the match.");
      nav("/v2/wc2026/ctf");
    } catch (e) { toast.error(formatApiError(e)); setBusy(false); }
  };

  if (loading || !room) {
    return <CtfShell><Loader2 className="w-6 h-6 animate-spin mx-auto mt-12 text-[#60A5FA]" /></CtfShell>;
  }

  return (
    <>
      <CtfShell title={`Match · Tier ${room.tier}`}>
        <RoomHeader room={room} />
        {room.status === "matched" && <AttestPhase room={room} busy={busy} onAttest={attest} />}
        {room.status === "attested" && <KickoffPhase room={room} busy={busy} scoreMine={scoreMine} setScoreMine={setScoreMine} scoreOpp={scoreOpp} setScoreOpp={setScoreOpp} completion={completion} setCompletion={setCompletion} onReport={report} />}
        {room.status === "reported" && <WaitingForOpponent room={room} busy={busy} scoreMine={scoreMine} setScoreMine={setScoreMine} scoreOpp={scoreOpp} setScoreOpp={setScoreOpp} completion={completion} setCompletion={setCompletion} onReport={report} />}
        {room.status === "awarded" && <AwardSummary room={room} />}
        {room.status === "closed" && <ClosedSummary room={room} />}
        {["matched", "attested", "reported"].includes(room.status) && (
          <button
            type="button"
            onClick={leave}
            disabled={busy}
            data-testid="ctf-leave-match"
            className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full border border-white/15 text-white/55 hover:text-white hover:border-white/30 text-[10px] font-mono uppercase tracking-widest disabled:opacity-50"
          >
            <X className="w-3 h-3" /> Leave match (opponent not responding?)
          </button>
        )}
        <CtfRoomChat roomId={roomId} closed={room.status === "closed"} />
      </CtfShell>
      {showReveal && room.me?.flag_awarded && (
        <FlagRevealOverlay
          nation={room.me.flag_awarded}
          rare={!!room.me.rare}
          tier={room.tier}
          onDone={() => { setShowReveal(false); nav("/v2/wc2026/ctf"); }}
        />
      )}
    </>
  );
}

function RoomHeader({ room }) {
  const tierBg = { 1: "#10B981", 2: "#FBBF24", 3: "#60A5FA" }[room.tier] || "#60A5FA";
  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-4" data-testid="ctf-room-header">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <div className="text-center">
            <div className="text-3xl">{NATION_EMOJI[room.me?.squad_nation] || "🏳️"}</div>
            <div className="text-[10px] font-mono text-white/60 mt-1">You</div>
            <div className="text-[10px] font-bold">{room.me?.squad_nation}</div>
          </div>
          <div className="text-white/40 text-[10px] font-mono">vs</div>
          <div className="text-center">
            <div className="text-3xl">{NATION_EMOJI[room.opp?.squad_nation] || "🏳️"}</div>
            <div className="text-[10px] font-mono text-white/60 mt-1">@{room.opp?.username}</div>
            <div className="text-[10px] font-bold">{room.opp?.squad_nation}</div>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-mono uppercase tracking-widest font-bold" style={{ backgroundColor: tierBg, color: "#020617" }}>
            Tier {room.tier}
          </span>
          <span className="text-[9px] font-mono uppercase tracking-widest text-white/40">{room.status}</span>
        </div>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2 text-[10px] font-mono">
        <div className={`rounded-md px-2 py-1.5 border ${room.me?.opp_flag_collected ? "bg-white/5 border-white/10 text-white/40" : "bg-[#10B981]/10 border-[#10B981]/40 text-[#10B981]"}`}>
          You earn: {room.me?.opp_flag_collected
            ? `${room.opp?.squad_nation} already collected → counts toward your streak`
            : `${room.opp?.squad_nation} flag`}
        </div>
        <div className={`rounded-md px-2 py-1.5 border ${room.opp?.my_flag_collected ? "bg-white/5 border-white/10 text-white/40" : "bg-[#10B981]/10 border-[#10B981]/40 text-[#10B981]"}`}>
          Opp earns: {room.opp?.my_flag_collected
            ? `${room.me?.squad_nation} already collected → counts toward their streak`
            : `${room.me?.squad_nation} flag`}
        </div>
      </div>
    </div>
  );
}

function AttestPhase({ room, busy, onAttest }) {
  return (
    <div className="rounded-2xl border border-[#FBBF24]/40 bg-[#FBBF24]/5 p-5 text-center" data-testid="ctf-attest-phase">
      <Camera className="w-8 h-8 text-[#FBBF24] mx-auto mb-3" />
      <div className="font-heading text-base uppercase tracking-wider">Pre-match attestation</div>
      <div className="text-[11px] text-white/65 mt-2 max-w-md mx-auto leading-relaxed">
        Build a <span className="text-white font-bold">pure {room.me.squad_nation}</span> 11 in eFootball.
        Tap below to confirm. Both players must attest before kickoff.
      </div>
      <div className="mt-3 flex items-center justify-center gap-2 text-[10px] font-mono">
        <span className={room.me.attested ? "text-[#10B981]" : "text-white/50"}>{room.me.attested ? "✓" : "○"} You</span>
        <span className="text-white/30">·</span>
        <span className={room.opp.attested ? "text-[#10B981]" : "text-white/50"}>{room.opp.attested ? "✓" : "○"} Opponent</span>
      </div>
      {!room.me.attested ? (
        <button
          type="button"
          onClick={onAttest}
          disabled={busy}
          data-testid="ctf-attest-confirm"
          className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#FBBF24] text-[#020617] text-[11px] font-mono uppercase tracking-widest font-bold hover:bg-[#F59E0B] disabled:opacity-50"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
          Confirm pure squad
        </button>
      ) : (
        <div className="mt-4 text-[11px] text-white/60">Waiting for opponent to attest…</div>
      )}
    </div>
  );
}

function KickoffPhase({ busy, scoreMine, setScoreMine, scoreOpp, setScoreOpp, completion, setCompletion, onReport }) {
  return (
    <div className="rounded-2xl border border-[#10B981]/40 bg-[#10B981]/5 p-5 text-center space-y-3" data-testid="ctf-kickoff-phase">
      <Trophy className="w-8 h-8 text-[#10B981] mx-auto" />
      <div className="font-heading text-base uppercase tracking-wider">Kick off!</div>
      <div className="text-[11px] text-white/65 max-w-md mx-auto">
        Play your match in eFootball. Suggested length: 6-minute halves.
        Report your score below when the match ends — win, draw or loss all earn flags.
      </div>
      <ScoreForm
        busy={busy} scoreMine={scoreMine} setScoreMine={setScoreMine}
        scoreOpp={scoreOpp} setScoreOpp={setScoreOpp}
        completion={completion} setCompletion={setCompletion}
        onReport={onReport}
      />
    </div>
  );
}

function WaitingForOpponent({ room, busy, scoreMine, setScoreMine, scoreOpp, setScoreOpp, completion, setCompletion, onReport }) {
  const ireported = !!room.me.reported;
  return (
    <div className="rounded-2xl border border-[#60A5FA]/40 bg-[#60A5FA]/5 p-5 text-center space-y-3" data-testid="ctf-waiting-phase">
      <Loader2 className="w-7 h-7 text-[#60A5FA] mx-auto animate-spin" />
      <div className="font-heading text-base uppercase tracking-wider">
        {ireported ? "Waiting for opponent score…" : "Submit your score"}
      </div>
      {!ireported && (
        <ScoreForm
          busy={busy} scoreMine={scoreMine} setScoreMine={setScoreMine}
          scoreOpp={scoreOpp} setScoreOpp={setScoreOpp}
          completion={completion} setCompletion={setCompletion}
          onReport={onReport}
        />
      )}
      {ireported && (
        <div className="text-[11px] text-white/55">Your score recorded ({room.me.score ?? "?"}–{room.me.opp_score ?? "?"}). Flag awards on opponent submission.</div>
      )}
    </div>
  );
}

function ScoreForm({ busy, scoreMine, setScoreMine, scoreOpp, setScoreOpp, completion, setCompletion, onReport }) {
  const [shot, setShot] = useState(null);
  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) { setShot(null); return; }
    if (f.size > 10 * 1024 * 1024) { toast.error("Screenshot too large (max 10MB)."); return; }
    setShot(f);
  };
  return (
    <div className="space-y-3 max-w-sm mx-auto">
      <div className="flex items-center justify-center gap-3">
        <NumberInput value={scoreMine} onChange={setScoreMine} label="You" />
        <span className="text-white/40 text-xl">–</span>
        <NumberInput value={scoreOpp} onChange={setScoreOpp} label="Opp" />
      </div>
      <div className="flex flex-col items-center gap-1">
        <label className="text-[10px] font-mono uppercase tracking-widest text-white/40">Match completion</label>
        <input
          type="range" min="0" max="100" value={completion} step="10"
          onChange={(e) => setCompletion(Number(e.target.value))}
          className="w-full max-w-xs accent-[#10B981]"
          data-testid="ctf-completion-slider"
        />
        <div className="text-[11px] font-mono">{completion}% — {completion >= 60 ? <span className="text-[#10B981]">flag eligible</span> : <span className="text-[#EF4444]">below 60% (no flag)</span>}</div>
      </div>
      {/* iter 97 — optional screenshot proof (sent via result-with-shot) */}
      <label className="flex items-center justify-center gap-2 cursor-pointer text-[10px] font-mono uppercase tracking-widest text-white/50 hover:text-white">
        <Camera className="w-3.5 h-3.5" />
        {shot ? `📎 ${shot.name.slice(0, 22)}` : "Attach screenshot (optional)"}
        <input type="file" accept="image/*" className="hidden" onChange={onPick} data-testid="ctf-result-shot" />
      </label>
      <button
        type="button"
        onClick={() => onReport(shot)}
        disabled={busy}
        data-testid="ctf-report-submit"
        className="mx-auto inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#10B981] text-[#020617] text-[11px] font-mono uppercase tracking-widest font-bold disabled:opacity-50 hover:bg-[#059669]"
      >
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
        Submit score
      </button>
    </div>
  );
}

function NumberInput({ value, onChange, label }) {
  return (
    <div className="flex flex-col items-center gap-1">
      <span className="text-[9px] font-mono uppercase tracking-widest text-white/40">{label}</span>
      <input
        type="number" min="0" max="30" value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={`ctf-score-${label.toLowerCase()}`}
        className="w-16 h-16 text-3xl text-center bg-white/5 border border-white/15 rounded-2xl font-bold focus:border-[#10B981] focus:outline-none"
      />
    </div>
  );
}

function AwardSummary({ room }) {
  return (
    <div className="rounded-2xl border border-[#FFD700]/40 bg-[#FFD700]/5 p-5 text-center space-y-2" data-testid="ctf-award-summary">
      <Award className="w-8 h-8 text-[#FFD700] mx-auto" />
      <div className="font-heading text-base uppercase tracking-wider">Match complete</div>
      <div className="text-[11px] text-white/70">
        {room.me?.flag_awarded
          ? <>You earned <span className="text-[#FFD700] font-bold">{NATION_EMOJI[room.me.flag_awarded] || "🏳️"} {room.me.flag_awarded}</span>{room.me.rare && " ★ Rare!"}</>
          : <>No new flag this match ({room.opp?.squad_nation} already in your album, or completion below 60%).</>
        }
      </div>
      <Link to="/v2/wc2026/ctf" data-testid="ctf-play-again" className="inline-flex items-center gap-1.5 mt-2 px-4 py-2 rounded-full bg-[#60A5FA] text-[#020617] text-[11px] font-mono uppercase tracking-widest font-bold hover:bg-[#3B82F6]">
        <Plus className="w-3.5 h-3.5" /> Play again
      </Link>
    </div>
  );
}

function ClosedSummary({ room }) {
  return (
    <div className="rounded-2xl border border-[#EF4444]/40 bg-[#EF4444]/5 p-5 text-center" data-testid="ctf-closed-summary">
      <X className="w-6 h-6 text-[#EF4444] mx-auto mb-2" />
      <div className="font-heading text-base uppercase tracking-wider">Match closed</div>
      <div className="text-[11px] text-white/60 mt-1">Reason: {room.close_reason || "unknown"} — no flag awarded.</div>
      <Link to="/v2/wc2026/ctf" data-testid="ctf-closed-back" className="inline-flex items-center gap-1.5 mt-3 px-4 py-2 rounded-full bg-white/10 text-white text-[10px] font-mono uppercase tracking-widest">
        Back to lobby
      </Link>
    </div>
  );
}

/* =================================================================== */
/* Flag-reveal overlay — pole-plant + unfurl animation                  */
/* =================================================================== */
function FlagRevealOverlay({ nation, rare, tier, onDone }) {
  const [stage, setStage] = useState(0);
  useEffect(() => {
    const t1 = setTimeout(() => setStage(1), 60);   // pole plant
    const t2 = setTimeout(() => setStage(2), 700);  // flag unfurls
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);
  return (
    <div className="fixed inset-0 z-[150] bg-[#020617]/95 backdrop-blur-md flex items-center justify-center p-4" data-testid="ctf-flag-reveal">
      <div className="text-center max-w-sm">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#60A5FA]/70">// Flag planted</div>
        <div className="font-heading text-xl uppercase tracking-wider mt-1">New flag earned!</div>
        {/* Animation stage */}
        <div className="relative h-44 mt-5 mx-auto w-40">
          {/* Pole */}
          <div className={`absolute left-1/2 -translate-x-1/2 bottom-0 w-1.5 bg-white/70 rounded-t origin-bottom transition-all duration-[700ms] ${stage >= 1 ? "h-40" : "h-0"}`} />
          {/* Flag */}
          <div
            className={`absolute left-1/2 top-2 transition-all duration-[600ms] ${stage >= 2 ? "opacity-100 scale-100" : "opacity-0 scale-50"}`}
            style={{ marginLeft: "3px" }}
          >
            <div className="text-[88px] leading-none drop-shadow-[0_8px_24px_rgba(96,165,250,0.55)]">{NATION_EMOJI[nation] || "🏳️"}</div>
          </div>
          {/* Ground */}
          <div className="absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r from-transparent via-white/20 to-transparent" />
        </div>
        <div className="font-heading text-2xl uppercase tracking-wider mt-3">{nation}</div>
        {rare && (
          <div className="mt-2 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#FBBF24] text-[#020617] text-[10px] font-mono uppercase tracking-widest font-bold">
            <Sparkles className="w-3 h-3" /> Rare nation bonus
          </div>
        )}
        <div className="mt-2 text-[10px] font-mono text-white/40">Earned via Tier {tier} match</div>
        <button
          type="button"
          onClick={onDone}
          data-testid="ctf-reveal-continue"
          className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#60A5FA] text-[#020617] text-[11px] font-mono uppercase tracking-widest font-bold hover:bg-[#3B82F6]"
        >
          Continue <ArrowRight className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

/* =================================================================== */
/* In-room chat                                                          */
/* =================================================================== */
function CtfRoomChat({ roomId, closed }) {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  const load = useCallback(async () => {
    try { const { data } = await api.get(`/ctf/room/${roomId}/chat`); setMsgs(data || []); }
    catch (e) { /* silent */ }
  }, [roomId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (closed) return;
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, [load, closed]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs.length]);
  const send = async (e) => {
    e?.preventDefault();
    if (!text.trim() || busy) return;
    setBusy(true);
    try {
      await api.post(`/ctf/room/${roomId}/chat`, { text: text.trim() });
      setText(""); load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-3" data-testid="ctf-chat">
      <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#60A5FA]/80 mb-2">// Chat</div>
      <div className="max-h-48 overflow-y-auto space-y-1.5 text-[11px]">
        {msgs.length === 0 && <div className="text-white/40 text-[10px]">Say hello to your flag-trade partner.</div>}
        {msgs.map((m) => (
          <div key={m.id} className="leading-tight">
            <span className="text-[#60A5FA] font-bold">@{m.username}</span>
            <span className="text-white/85 ml-1.5">{m.text}</span>
          </div>
        ))}
        <div ref={endRef} />
      </div>
      {!closed && (
        <form onSubmit={send} className="mt-2 flex items-center gap-1.5">
          <input
            type="text" value={text} onChange={(e) => setText(e.target.value)}
            placeholder="Type a message…" maxLength={400}
            data-testid="ctf-chat-input"
            className="flex-1 bg-white/5 border border-white/15 rounded-full px-3 py-1.5 text-[11px] focus:border-[#60A5FA] focus:outline-none"
          />
          <button type="submit" disabled={busy || !text.trim()} data-testid="ctf-chat-send" className="rounded-full bg-[#60A5FA] text-[#020617] px-3 py-1.5 text-[11px] disabled:opacity-40">
            <Send className="w-3 h-3" />
          </button>
        </form>
      )}
    </div>
  );
}

/* =================================================================== */
/* Named export: <CtfAlbum> — full 48-flag grid                          */
/* =================================================================== */
export function CtfAlbum() {
  const [platform, setPlatform] = useState("mobile");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    (async () => {
      try { const { data: d } = await api.get(`/ctf/album?platform=${platform}`); setData(d); }
      catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, [platform]);
  if (loading || !data) {
    return <CtfShell title="Flag Album"><Loader2 className="w-6 h-6 animate-spin mx-auto mt-12 text-[#60A5FA]" /></CtfShell>;
  }
  return (
    <CtfShell title="Flag Album" backTo="/v2/wc2026/ctf">
      <PlatformSwitcher value={platform} onChange={setPlatform} />
      <div className="rounded-3xl border border-[#60A5FA]/30 bg-gradient-to-br from-[#1E3A8A] via-[#0F172A] to-[#020617] p-5 text-center" data-testid="ctf-album-hero">
        <div className="font-heading text-3xl uppercase tracking-wider">{data.count}<span className="text-white/40">/48</span></div>
        <div className="text-[11px] text-white/60 mt-1">{data.matches_played} matches · best streak {data.streak_best}</div>
        {data.complete && (
          <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#FFD700] text-[#020617] text-[10px] font-mono uppercase tracking-widest font-bold">
            <Trophy className="w-3.5 h-3.5" /> Collection complete
          </div>
        )}
      </div>
      <div className="grid grid-cols-4 sm:grid-cols-6 gap-2" data-testid="ctf-album-grid">
        {data.grid.map((g) => (
          <div
            key={g.nation}
            data-testid={`ctf-album-cell-${g.nation}`}
            className={`relative flex flex-col items-center gap-1 rounded-lg px-2 py-2 border text-[10px] ${
              g.held
                ? "bg-white/8 border-[#60A5FA]/40 text-white"
                : "bg-white/[0.02] border-white/5 text-white/25"
            }`}
          >
            <span className={`text-2xl leading-none transition ${g.held ? "" : "grayscale opacity-40"}`}>{g.flag}</span>
            <span className="truncate w-full text-center">{g.held ? g.nation : "???"}</span>
            {g.rare && g.held && <span className="absolute top-1 right-1 text-[8px] text-[#FBBF24]">★</span>}
            {g.held && g.tier && (
              <span className="absolute -top-1 -left-1 inline-flex items-center justify-center w-4 h-4 rounded-full bg-[#020617] border border-[#60A5FA] text-[8px] font-bold text-[#60A5FA]">{g.tier}</span>
            )}
          </div>
        ))}
      </div>
      <div className="text-[10px] text-white/40 mt-2 px-1">Tap a flag for details · ★ = rare nation · number badge = match tier the flag was earned in</div>
    </CtfShell>
  );
}

/* =================================================================== */
/* Nation emoji lookup (kept local to avoid an extra fetch on every load) */
/* =================================================================== */
const NATION_EMOJI = {
  "Mexico": "🇲🇽", "South Africa": "🇿🇦", "Korea Republic": "🇰🇷", "Czechia": "🇨🇿",
  "Canada": "🇨🇦", "Bosnia & Herzegovina": "🇧🇦", "Qatar": "🇶🇦", "Switzerland": "🇨🇭",
  "Brazil": "🇧🇷", "Morocco": "🇲🇦", "Haiti": "🇭🇹", "Scotland": "🏴󠁧󠁢󠁳󠁣󠁴󠁿",
  "USA": "🇺🇸", "Paraguay": "🇵🇾", "Australia": "🇦🇺", "Türkiye": "🇹🇷",
  "Germany": "🇩🇪", "Curaçao": "🇨🇼", "Côte d'Ivoire": "🇨🇮", "Ecuador": "🇪🇨",
  "Netherlands": "🇳🇱", "Japan": "🇯🇵", "Sweden": "🇸🇪", "Tunisia": "🇹🇳",
  "Belgium": "🇧🇪", "Egypt": "🇪🇬", "Iran": "🇮🇷", "New Zealand": "🇳🇿",
  "Spain": "🇪🇸", "Cabo Verde": "🇨🇻", "Saudi Arabia": "🇸🇦", "Uruguay": "🇺🇾",
  "France": "🇫🇷", "Senegal": "🇸🇳", "Iraq": "🇮🇶", "Norway": "🇳🇴",
  "Argentina": "🇦🇷", "Algeria": "🇩🇿", "Austria": "🇦🇹", "Jordan": "🇯🇴",
  "Portugal": "🇵🇹", "Congo DR": "🇨🇩", "Uzbekistan": "🇺🇿", "Colombia": "🇨🇴",
  "England": "🏴󠁧󠁢󠁥󠁮󠁧󠁿", "Croatia": "🇭🇷", "Ghana": "🇬🇭", "Panama": "🇵🇦",
};
